import { useCallback, useReducer, useRef, useState } from "react"

import { getConversationMessages, getSuggestions, streamChat } from "@/lib/api"
import { parseSSEStream } from "@/lib/sse"
import { cleanLLMText, searchingLabel } from "@/lib/text"
import type { ChatStreamEvent, FileAttachment, MeetingContext, MeetingRange, SearchScope, Source } from "@/types/api"

export interface ChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  status: "searching" | "generating-image" | "streaming" | "done" | "error"
  searchingLabel?: string
  /** What the running tool is doing right now (e.g. the Work IQ queries). */
  searchingDetail?: string[]
  attachment?: { type: "document" | "image"; filename: string }
  sources?: Source[]
  lowConfidence?: boolean
  agentLabel?: string | null
  /** Engine that actually answered, and why it differs from the one picked. */
  retrievalEngine?: string
  retrievalNotice?: string | null
  /** Why a topic scope established earlier in the conversation was dropped. */
  scopeNotice?: string | null
  /** Mail/Teams was switched on but could not be searched. */
  personalNotice?: string | null
  suggestions?: string[]
  image?: { url: string; prompt: string }
  errorMessage?: string
}

interface ChatState {
  messages: ChatMessage[]
  isStreaming: boolean
}

type Action =
  | { type: "reset" }
  | { type: "load"; messages: ChatMessage[] }
  | { type: "send"; user: ChatMessage; assistantId: string }
  | { type: "searching"; id: string; label: string; detail?: string[] }
  | { type: "generating-image"; id: string }
  | { type: "token"; id: string; content: string }
  | { type: "image"; id: string; url: string; prompt: string }
  | {
      type: "done"
      id: string
      sources: Source[]
      lowConfidence: boolean
      agentLabel: string | null
      retrievalEngine?: string
      retrievalNotice?: string | null
      scopeNotice?: string | null
      personalNotice?: string | null
    }
  | { type: "suggestions"; id: string; suggestions: string[] }
  | { type: "error"; id: string; message?: string }
  | { type: "aborted"; id: string }
  | { type: "stream-finished" }

function patchMessage(state: ChatState, id: string, patch: Partial<ChatMessage>): ChatState {
  return {
    ...state,
    messages: state.messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
  }
}

function reducer(state: ChatState, action: Action): ChatState {
  switch (action.type) {
    case "reset":
      return { messages: [], isStreaming: false }
    case "load":
      return { messages: action.messages, isStreaming: false }
    case "send":
      return {
        isStreaming: true,
        messages: [
          ...state.messages,
          action.user,
          { id: action.assistantId, role: "assistant", content: "", status: "streaming" },
        ],
      }
    case "searching":
      return patchMessage(state, action.id, {
        status: "searching",
        searchingLabel: action.label,
        searchingDetail: action.detail,
      })
    case "generating-image":
      return patchMessage(state, action.id, { status: "generating-image" })
    case "token": {
      const message = state.messages.find((m) => m.id === action.id)
      if (!message) return state
      return patchMessage(state, action.id, {
        status: "streaming",
        content: message.content + action.content,
      })
    }
    case "image":
      return patchMessage(state, action.id, {
        status: "done",
        image: { url: action.url, prompt: action.prompt },
      })
    case "done": {
      const message = state.messages.find((m) => m.id === action.id)
      if (!message) return state
      return patchMessage(state, action.id, {
        status: "done",
        content: cleanLLMText(message.content),
        sources: action.sources,
        lowConfidence: action.lowConfidence,
        agentLabel: action.agentLabel,
        retrievalEngine: action.retrievalEngine,
        retrievalNotice: action.retrievalNotice,
        scopeNotice: action.scopeNotice,
        personalNotice: action.personalNotice,
      })
    }
    case "suggestions":
      return patchMessage(state, action.id, { suggestions: action.suggestions })
    case "error":
      return patchMessage(state, action.id, { status: "error", errorMessage: action.message })
    case "aborted": {
      const message = state.messages.find((m) => m.id === action.id)
      if (!message) return state
      // Nothing streamed yet → drop the empty placeholder; else keep the partial text
      if (!message.content && !message.image) {
        return { ...state, messages: state.messages.filter((m) => m.id !== action.id) }
      }
      return patchMessage(state, action.id, { status: "done" })
    }
    case "stream-finished":
      return { ...state, isStreaming: false }
    default:
      return state
  }
}

export interface SendOptions {
  attachment?: FileAttachment | null
  agentId?: string | null
  modelId?: string | null
  effort?: string | null
  retrievalEngine?: string | null
  scopes?: SearchScope[]
  projectId?: string | null
  meetingRange?: MeetingRange | null
  meeting?: MeetingContext | null
}

export function useChatStream(options: { onConversationCreated?: (id: string) => void; onTurnDone?: () => void } = {}) {
  const [state, dispatch] = useReducer(reducer, { messages: [], isStreaming: false })
  const [conversationId, setConversationId] = useState<string | null>(null)
  const conversationIdRef = useRef<string | null>(null)
  const projectIdRef = useRef<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const onCreatedRef = useRef(options.onConversationCreated)
  onCreatedRef.current = options.onConversationCreated
  const onTurnDoneRef = useRef(options.onTurnDone)
  onTurnDoneRef.current = options.onTurnDone

  const setConversation = useCallback((id: string | null) => {
    conversationIdRef.current = id
    setConversationId(id)
  }, [])

  const fetchSuggestionsFor = useCallback(
    (assistantId: string, question: string, answer: string, sources: Source[], modelId: string | null) => {
      getSuggestions({
        question,
        response: answer.slice(0, 600),
        source_titles: sources.map((s) => s.title),
        model_id: modelId,
      })
        .then((suggestions) => {
          if (suggestions.length) dispatch({ type: "suggestions", id: assistantId, suggestions })
        })
        .catch(() => {
          // suggestions are best-effort
        })
    },
    [],
  )

  const send = useCallback(
    async (
      text: string,
      {
        attachment = null,
        agentId = null,
        modelId = null,
        effort = null,
        retrievalEngine = null,
        scopes = ["sharepoint"],
        projectId,
        meetingRange = null,
        meeting = null,
      }: SendOptions = {},
    ) => {
      const trimmed = text.trim()
      if (!trimmed || abortRef.current) return
      const requestProjectId = projectId !== undefined ? projectId : projectIdRef.current

      const assistantId = crypto.randomUUID()
      const isNew = !conversationIdRef.current
      dispatch({
        type: "send",
        assistantId,
        user: {
          id: crypto.randomUUID(),
          role: "user",
          content: trimmed,
          status: "done",
          attachment: attachment ? { type: attachment.type, filename: attachment.filename } : undefined,
        },
      })

      const controller = new AbortController()
      abortRef.current = controller
      let answer = ""

      try {
        const response = await streamChat(
          {
            message: trimmed,
            conversation_id: conversationIdRef.current,
            file_attachment: attachment,
            agent_id: agentId,
            model_id: modelId,
            effort,
            retrieval_engine: retrievalEngine,
            scopes,
            project_id: requestProjectId,
            meeting_range: meetingRange,
            meeting,
          },
          controller.signal,
        )

        for await (const event of parseSSEStream<ChatStreamEvent>(response)) {
          switch (event.type) {
            case "searching":
              dispatch({
                type: "searching",
                id: assistantId,
                label: searchingLabel(event.mode, event.label, event.engine),
                detail: event.detail,
              })
              break
            case "generating_image":
              dispatch({ type: "generating-image", id: assistantId })
              break
            case "token":
              answer += event.content
              dispatch({ type: "token", id: assistantId, content: event.content })
              break
            case "image":
              dispatch({ type: "image", id: assistantId, url: event.url, prompt: event.prompt })
              setConversation(event.conversation_id)
              projectIdRef.current = requestProjectId
              if (isNew) onCreatedRef.current?.(event.conversation_id)
              break
            case "done": {
              const sources = event.sources ?? []
              dispatch({
                type: "done",
                id: assistantId,
                sources,
                lowConfidence: event.low_confidence,
                agentLabel: event.agent_label,
                retrievalEngine: event.retrieval_engine,
                retrievalNotice: event.retrieval_notice,
                scopeNotice: event.scope_notice,
                personalNotice: event.personal_notice,
              })
              setConversation(event.conversation_id)
              if (event.project_id !== undefined) projectIdRef.current = event.project_id
              if (isNew) onCreatedRef.current?.(event.conversation_id)
              onTurnDoneRef.current?.()
              if (answer) fetchSuggestionsFor(assistantId, trimmed, answer, sources, modelId)
              break
            }
            case "error":
              dispatch({ type: "error", id: assistantId, message: event.message })
              break
          }
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") {
          dispatch({ type: "aborted", id: assistantId })
        } else {
          console.error("Streaming error:", error)
          dispatch({
            type: "error",
            id: assistantId,
            message: error instanceof Error && error.message ? error.message : undefined,
          })
        }
      } finally {
        abortRef.current = null
        dispatch({ type: "stream-finished" })
      }
    },
    [fetchSuggestionsFor, setConversation],
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    setConversation(null)
    projectIdRef.current = null
    dispatch({ type: "reset" })
  }, [setConversation])

  /** Loads a stored thread. Resolves to the agent id of its latest assistant
   *  turn (or null), so the caller can restore that conversation's topic. */
  const loadConversation = useCallback(
    async (id: string): Promise<string | null> => {
      abortRef.current?.abort()
      setConversation(id)
      projectIdRef.current = null
      try {
        const stored = await getConversationMessages(id)
        dispatch({
          type: "load",
          messages: stored.map((m) => ({
            id: crypto.randomUUID(),
            role: m.role === "user" ? "user" : "assistant",
            content: m.content,
            status: "done" as const,
            // Structured fields persisted with the turn — the reloaded thread
            // renders the same sources card and engine attribution as live.
            sources: m.sources,
            retrievalEngine: m.retrieval_engine,
            retrievalNotice: m.retrieval_notice ?? undefined,
            scopeNotice: m.scope_notice ?? undefined,
            personalNotice: m.personal_notice ?? undefined,
          })),
        })
        const lastWithAgent = [...stored].reverse().find((m) => m.role === "assistant" && m.agent_id)
        return lastWithAgent?.agent_id ?? null
      } catch {
        dispatch({ type: "load", messages: [] })
        return null
      }
    },
    [setConversation],
  )

  return {
    messages: state.messages,
    isStreaming: state.isStreaming,
    conversationId,
    send,
    stop,
    reset,
    loadConversation,
  }
}
